<script>
import { getX, getY, xBar, yBar, findEdgeDates, countArrowPoints, getDiffInHours, validateForLoop, settings } from '@/model/gantt/ganttUtils'; // eslint-disable-line

import GanttBar from '@/components/gantt/GanttBar.vue';
import GanttHeader from '@/components/gantt/GanttHeader.vue';
import GanttGrid from '@/components/gantt/GanttGrid.vue';
import GanttScaleFactory from '@/model/gantt/GanttScale';
import GanttArrow from '@/components/gantt/GanttArrow.vue';
import GanttTermSelector from '@/components/gantt/GanttTermSelector.vue';

export default {
  name: 'GanttChart',
  components: { GanttTermSelector, GanttArrow, GanttGrid, GanttHeader, GanttBar }, // eslint-disable-line
  props: {
    data: {
      type: Array,
      default() { return []; },
    },
    binds: {
      type: Array,
      default() { return []; },
    },
    scale: String,
  },
  data() {
    return {
      scrollDate: null,
      trackScroll: true,
      mouseTask: null,
      mouseInitialPoint: null,
      scrollInitial: null,
      verticalHoverIndex: null,
      headerShift: 0,
      draggable: {
        item: null,
        type: null,
        xDelta: null,
      },
      binding: {
        from: null,
        isStart: null,
        pointStart: null,
        pointFinish: null,
      },
      termSelectingBarId: null,
      termSelectingPosX: null,
      hovered: null,
      test: 0,
    };
  },
  computed: {
    sizes() { return settings.sizes; },
    scaleInstance() { return (new GanttScaleFactory()).create(this.scale, ...findEdgeDates(this.data)); },
    header() { return this.scaleInstance.createHeader(30); },
    today() {
      const width = 22;
      const padding = (settings.sizes.cell.w - width) / 2;
      const x = getX(this.scaleInstance.getCell(new Date())) + padding;
      return { x, width };
    },
    verticalHover() {
      if (this.verticalHoverIndex === null) {
        return null;
      }

      const width = 22;
      const padding = (settings.sizes.cell.w - width) / 2;
      const x = getX(this.verticalHoverIndex) + padding;
      return { x, width };
    },
    holidays() {
      const width = 22;
      const padding = (settings.sizes.cell.w - width) / 2;

      return this.scaleInstance.getHolidays().map(({ i }) => getX(i) + padding);
    },
    bars() {
      let y = 0;

      const countCoordinates = (item) => {
        const x = this.scaleInstance.getCell(item.dateStart);
        const xf = this.scaleInstance.getCell(item.dateFinish);
        const w = xf - x + 1;

        const res = {
          index: { x, y, w },
          y: getY(y),
          x: getX(x),
          w: getX(w),
        };
        y += 1;
        return res;
      };

      const bars = [];
      const flattenRecursive = (items, hidden = false) => {
        items.forEach(item => {
          bars.push({
            type: item['@type'],
            self: item,
            hidden,
            ...(hidden ? {} : countCoordinates(item)),
          });

          if (item.children?.length) {
            flattenRecursive(item.children, hidden || item.collapsed);
          }
        });
      };

      flattenRecursive(this.data);

      return bars;
    },

    staticBars() { return this.bars.filter(b => b.self['@id'] !== this.draggable.item?.self?.['@id']); },
    termSelectingBar() { return this.bars.find(b => b.self['@id'] === this.termSelectingBarId); },

    draggableIndex() {
      const { item, type, xDelta } = this.draggable;
      let { x, w } = item?.index ?? { x: 0, w: 0 };

      switch (type) {
        case 'center':
          x += xDelta;
          break;
        case 'start':
          x = Math.min(x + xDelta, x + w - 1);
          w = Math.max(w - xDelta, 1);
          break;
        case 'finish':
          w = Math.max(w + xDelta, 1);
          break;
        default:
          break;
      }

      return { x, w };
    },
    draggablePos() { return { x: getX(this.draggableIndex.x), w: getX(this.draggableIndex.w) }; },
    draggableBar() { return this.draggable.item ? { ...this.draggable.item, ...this.draggablePos } : null; },

    arrows() {
      return countArrowPoints(
        this.binds
          .map(bind => ({
            id: bind.id,
            isStart: bind.isStart,
            from: this.bars.find(bar => bar.self['@id'] === bind.from),
            to: this.bars.find(bar => bar.self['@id'] === bind.to),
          }))
          .filter(b => b.from && b.to && !b.from.hidden && !b.to.hidden),
      );
    },

    width() { return this.header.bottom[this.header.bottom.length - 1]?.x + settings.sizes.cell.w; },
    height() {
      const lastVisibleBar = this.bars.findLast(b => !b.hidden) ?? {};
      return lastVisibleBar?.y + settings.sizes.cell.h + settings.sizes.header.h; },
    contentHeight() { return this.height - settings.sizes.header.h * 2 - 10; },
    viewBox() { return `0 0 ${this.width} ${this.height}`; },
    svgCursor() {
      if (this.draggable.item) return 'ew-resize';
      if (this.mouseTask === 'scroll') return 'grabbing';

      return 'auto';
    },
  },
  watch: {
    scaleInstance: {
      handler() {
        this.scrollToDate();
      },
    },
  },
  methods: {
    svgMouseDown(e) {
      this.mouseTask = 'scroll';

      this.mouseUpListenerAdd();
      this.mouseMoveListenerAdd();
    },

    svgClick() {
      this.dropTempBind();
      this.termSelectingBarId = null;
    },

    dropTempBind() {
      this.binding.isStart = null;
      this.binding.from = null;
      this.binding.pointStart = null;
      this.binding.pointFinish = null;
      this.mouseMoveListenerRemove();
    },

    createBind(toId) {
      const { from, isStart } = this.binding;
      const fromId = from.self['@id'];

      const hasLoops = validateForLoop(this.binds, fromId, toId);
      if (hasLoops) {
        this.$alert('Между этими задачами уже существует связь');
        return;
      }

      this.$emit('bind', { isStart, to: toId, from: fromId });
    },

    bind({ id, isStart }) {
      if (this.binding.from) {
        if (isStart) {
          this.createBind(id);
        } else {
          this.$alert('Нельзя связывать с концом задачи');
        }

        this.dropTempBind();
        return;
      }

      const bar = this.bars.find(b => b.self['@id'] === id);
      this.binding.from = bar;
      this.binding.isStart = isStart;
      this.binding.pointStart = [xBar(bar, isStart ? 'start' : 'finish'), yBar(bar)];
      this.mouseMoveListenerAdd();
      this.mouseTask = 'connect';
    },

    updateBars(items, deltaStart, deltaFinish) {
      const itemsToUpdate = [];

      const collectBarsRecursive = (id, diff, isStart = null) => {
        if (!diff) return;

        this.binds
          .filter(b => b.from === id && (isStart === null || b.isStart === isStart))
          .forEach(bind => {
            const item = this.bars.find(b => b.self['@id'] === bind.to);
            if (!item) return;
            if (!item.self.isEditable) return;

            let { dateStart, dateFinish } = item.self;
            dateStart = new Date(dateStart.getTime() + diff);
            dateFinish = new Date(dateFinish.getTime() + diff);
            itemsToUpdate.push({ item: item.self, dateStart, dateFinish });
            collectBarsRecursive(item.self['@id'], diff);
          });
      };

      items.forEach((item) => {
        if (!item.isEditable) return;

        let { dateStart, dateFinish } = item;

        const itemAlreadyExists = !!itemsToUpdate.find(i => i.item['@id'] === item['@id']);
        if (itemAlreadyExists) return;

        dateStart = new Date(dateStart.getTime() + deltaStart);
        dateFinish = new Date(dateFinish.getTime() + deltaFinish);

        itemsToUpdate.push({ item, dateStart, dateFinish });

        collectBarsRecursive(item['@id'], deltaStart, true);
        collectBarsRecursive(item['@id'], deltaFinish, false);
      });

      // console.log('emit update', itemsToUpdate.map(i => i.item.name));
      this.$emit('update', itemsToUpdate);
    },

    updateDraggedBar() {
      if (this.mouseTask !== 'drag') return;

      const { x, w } = this.draggableIndex;
      const { index, type, self } = this.draggable.item; // eslint-disable-line

      if (x === index.x && w === index.w) {
        return;
      }

      const newDateStart = this.scaleInstance.countDate(self.dateStart, x - index.x);
      const newDateFinish = this.scaleInstance.countDate(self.dateFinish, x + w - index.w - index.x);
      const deltaStart = newDateStart - self.dateStart;
      const deltaFinish = newDateFinish - self.dateFinish;

      if (type === 'Stage') {
        this.updateBars(self.children, deltaStart, deltaStart); // not an error, both params are start
      } else {
        this.updateBars([self], deltaStart, deltaFinish);
      }
    },

    dragBar({ item, type }) {
      this.mouseTask = 'drag';
      this.draggable.item = item;
      this.draggable.type = type;

      this.termSelectingBarId = null;
      this.mouseUpListenerAdd();
      this.mouseMoveListenerAdd();
    },
    clickBar(bar) {
      if (this.mouseInitialPoint) return;
      this.termSelectingBarId = bar.self['@id'];
      this.termSelectingPosX = getX(this.verticalHoverIndex);
    },

    mouseMove(e) {
      // use clientX, pageX or x, not offsetX, screenX
      const { x: screenX, y: screenY } = e;

      if (!this.mouseInitialPoint) {
        this.mouseInitialPoint = [screenX, screenY];
        return;
      }

      switch (this.mouseTask) {
        case 'drag':
          this.draggable.xDelta = Math.round((screenX - this.mouseInitialPoint[0]) / settings.sizes.cell.w);
          break;
        case 'connect':
          const x = screenX - this.mouseInitialPoint[0]; // eslint-disable-line no-case-declarations
          const y = screenY - this.mouseInitialPoint[1]; // eslint-disable-line no-case-declarations
          this.binding.pointFinish = [
            this.binding.pointStart[0] + x + (x > 0 ? -0.1 : 0.1),
            this.binding.pointStart[1] + y + (y > 0 ? -0.1 : 0.1),
          ];
          break;
        case 'scroll':
          if (!this.scrollInitial) {
            this.scrollInitial = this.$refs.container.scrollLeft;
          }
          const factor = e.ctrlKey ? 3 : 1; // eslint-disable-line
          const delta = (this.mouseInitialPoint[0] - screenX) * factor;  // eslint-disable-line
          this.$refs.container.scrollLeft = this.scrollInitial + delta;
          break;
        default: break;
      }
    },

    mouseUp() {
      this.updateDraggedBar();
      this.mouseUpListenerRemove();
      this.mouseMoveListenerRemove();
      this.draggable.xDelta = null;
      this.draggable.item = null;
      this.draggable.type = null;
    },
    mouseUpListenerAdd() { document.addEventListener('mouseup', this.mouseUp); },
    mouseUpListenerRemove() { document.removeEventListener('mouseup', this.mouseUp); },
    mouseMoveListenerAdd() { document.addEventListener('mousemove', this.mouseMove); },
    mouseMoveListenerRemove() { document.removeEventListener('mousemove', this.mouseMove); this.mouseInitialPoint = null; this.mouseTask = null; this.scrollInitial = null; },

    svgScroll() {
      if (!this.trackScroll) return;
      const i = Math.ceil((this.$el.scrollLeft + (this.$el.clientWidth / 2)) / settings.sizes.cell.w);
      this.scrollDate = this.scaleInstance.countDate(this.scaleInstance.startDate, i);
    },

    scrollToDate() {
      this.trackScroll = false;
      setTimeout(() => {
        this.$refs.container.scrollLeft = (this.scaleInstance.getCell(this.scrollDate) * settings.sizes.cell.w) - (this.$el.clientWidth / 2);
        this.trackScroll = true;
      }, 0);
    },

    onMouseMove(e) {
      this.verticalHoverIndex = Math.floor(e.offsetX / settings.sizes.cell.w);
    },

    svgMouseLeave() {
      this.verticalHoverIndex = null;
    },

    documentScroll() {
      const min = Math.min(0, this.$refs.container.getBoundingClientRect().top);
      this.headerShift = -min;
    },
  },
  mounted() {
    this.scrollDate = new Date();
    document.addEventListener('scroll', this.documentScroll);
    this.$refs.container.scrollLeft = this.header.curr.x - (this.$refs.container.getBoundingClientRect().width / 2);
  },
  beforeDestroy() {
    this.mouseUpListenerRemove();
    this.mouseMoveListenerRemove();
    document.removeEventListener('scroll', this.documentScroll);
  },
};
</script>

<template lang="pug">
  div.gantt-chart(v-if="data" ref="container" @scroll="svgScroll")
    .term-selector(v-if="termSelectingBar" :style="{ left: `${termSelectingPosX + sizes.cell.w / 2}px`, top: `${termSelectingBar.y}px` }")
      GanttTermSelector(
        :dateType="termSelectingBar.self['@type'] === 'Project' ? 'date' : 'datetime'"
        :dateStart="termSelectingBar.self.dateStart"
        :dateFinish="termSelectingBar.self.dateFinish"
        @change="updateBars([termSelectingBar.self], $event[0], $event[1])"
        )
    svg(:width="width" :height="height" :viewBox="viewBox" :style="{ cursor: svgCursor }" ref="svg" @click="svgClick" @mousedown="svgMouseDown" @mousemove="onMouseMove" @mouseleave="svgMouseLeave")
      g
        rect(x="0" y="0" :width="width" :height="height" fill="#F8F9FC")
      g(id="grid")
        template(v-for="(item, i) in header.bottom")
          rect(v-if="i > 0" fill="#E9F0F5" :x="item.x" :y="70" :height="contentHeight" width="1")
        rect(v-for="x in holidays" fill="#EBF1F7" :y="72" :height="contentHeight - 4" :x="x" :width="22")
        rect(v-if="verticalHover" fill="#FCF7EA" :y="72" :height="contentHeight - 4" :x="verticalHover.x" :width="verticalHover.width")
        rect(fill="#FFFADC" :y="72" :height="contentHeight - 4" :x="today.x" :width="today.width")
        rect.bar-hover(v-for="item in bars" :x="0" :y="item.y" :height="40" :width="width" :class="{ active: item.self['@id'] === hovered }")
      g
        GanttArrow(v-for="item in arrows" :key="item.id" :points="item.points" @delete="$emit('unbind', item.id)")
      g
        template(v-for="bar in [...staticBars, draggableBar]")
          GanttBar(
            v-if="bar && !bar.hidden"
            :key="bar.self['@id']"
            :item="bar"
            @drag="dragBar"
            @click.native.stop
            @bind="bind"
            @mouseup="clickBar(bar)"
            fill="black"
            @enter="hovered = bar.self['@id']"
            @leave="hovered = null"
          )
      polyline(v-if="binding.pointStart && binding.pointFinish" :points="`${binding.pointStart.join(',')} ${binding.pointFinish.join(',')}`" style="stroke: rgb(255, 160, 17); stroke-width: 2.5px; fill: none;")
      GanttHeader(:header="header" :contentHeight="height - this.sizes.header.h" :headerShift="headerShift" :width="width")
</template>

<style scoped lang="scss">
  .gantt-chart {
    width: 100%;
    overflow-x: scroll;
    position: relative;
  }

  .term-selector {
    position: absolute;
  }

  svg {
    user-select: none;
  }

  .bar-hover {
    fill: transparent;

    &:hover, &.active {
      fill: #EFF7FF;
    }
  }
</style>
